import debounce from 'lodash/debounce';

import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { NonErrorFallbackState } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';

// eslint-disable-next-line import/order
import { addAnalytics } from './utils';
// eslint-disable-next-line import/order
import type { DebouncedFunc } from 'lodash';

let mergedNonErrorFallbackState: NonErrorFallbackState = {};

/**
 * Merges a new state into the existing merged state, maintaining the maximum count for each key.
 * @param {object} newState - New state to merge.
 */
export const mergeNonErrorFallbackState = (newState: NonErrorFallbackState): void => {
	for (const [key, value] of Object.entries(newState)) {
		if (mergedNonErrorFallbackState[key] === undefined) {
			mergedNonErrorFallbackState[key] = value;
		} else {
			mergedNonErrorFallbackState[key] = Math.max(mergedNonErrorFallbackState[key], value);
		}
	}
};

/**
 * Dispatches an analytics event with the current merged state.
 * sliceOrNodeToMarkdown is used in multiples places, and we don't want to send multiple analytics events
 * for the same nonErrorFallbackState, so we debounce it.
 *
 * We also want to merge the nonErrorFallbackState with the existing state
 * @param {EditorView} editorView - The editor view instance.
 */
export const dispatchNonErrorFallbackStateAnalyticsEvent = (
	editorView: EditorView,
	aiSessionId: string,
): void => {
	const tr = addAnalytics({
		editorState: editorView.state,
		tr: editorView.state.tr,
		payload: {
			action: 'nonErrorFallback',
			actionSubject: 'editorPluginAI',
			actionSubjectId: 'markdownSerializer',
			attributes: {
				aiSessionId,
				nonErrorFallbackState: mergedNonErrorFallbackState,
			},
			eventType: EVENT_TYPE.OPERATIONAL,
		},
	});
	tr.setMeta('addToHistory', false);
	editorView.dispatch(tr);
	// Reset the merged state
	mergedNonErrorFallbackState = {};
};

export const debounceDispatchNonErrorFallbackStateAnalyticsEvent: DebouncedFunc<
	(editorView: EditorView, aiSessionId: string) => void
> = debounce(dispatchNonErrorFallbackStateAnalyticsEvent, 500);
